import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Download, 
  Brain, 
  Camera,
  Users,
  Trophy,
  MessageCircle,
  TrendingUp,
  Star,
  ArrowRight,
  Heart,
  Share2,
  Play,
  Calendar,
  Target,
  Zap,
  Globe,
  Smartphone,
  CheckCircle,
  Sparkles,
  ChevronDown
} from 'lucide-react';
import logo from '../assets/logo.png';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
};

// Scroll Reveal Component
const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`reveal ${isVisible ? 'active' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

// Floating Particle Component
const FloatingParticle = ({ delay, duration, left, size }) => {
  return (
    <div
      className="particle absolute rounded-full bg-white/10 blur-sm"
      style={{
        left: `${left}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
    />
  );
};

const LandingPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const newFeatures = [
    {
      icon: Camera,
      title: '📸 Highlight Feed',
      description: 'Watch & share goals, saves, cellys, and more from your club or any player you follow.',
      color: 'from-pink-500 to-red-500',
      delay: 0
    },
    {
      icon: Calendar,
      title: '📊 Season Tracker',
      description: 'Log every stat and video, season by season, so you\'ll never lose those memories.',
      color: 'from-blue-500 to-cyan-500',
      delay: 100
    },
    {
      icon: Globe,
      title: '🌐 Cross-Organization Following',
      description: 'Keep tabs on buddies who switch teams or crush summer leagues—you\'ll see their progress in one place.',
      color: 'from-green-500 to-emerald-500',
      delay: 200
    },
    {
      icon: Trophy,
      title: '🏆 Game Results',
      description: 'League scores, tournament wins, and clutch moments appear right in your feed so you can drop a quick "Nice dub!"',
      color: 'from-yellow-500 to-orange-500',
      delay: 300
    },
    {
      icon: MessageCircle,
      title: '💬 Reactions & Comments',
      description: 'Hype up teammates, ask for camp advice, or share stick-tap emojis with the whole community.',
      color: 'from-purple-500 to-pink-500',
      delay: 400
    },
    {
      icon: Brain,
      title: '🤖 Coach Biscuit (AI Chat)',
      description: 'Need fresh drills, strategy tips, or even pointers on dropping the gloves? Ask and get instant, personalized advice.',
      color: 'from-indigo-500 to-purple-500',
      delay: 500
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Everything Hockey, Zero Clutter',
      description: 'No more digging through social media noise.',
      delay: 0
    },
    {
      icon: Heart,
      title: 'Memories That Last',
      description: 'Relive each season\'s soundtrack of buzzer-beaters and hat tricks.',
      delay: 100
    },
    {
      icon: Users,
      title: 'Community Built for Players & Parents',
      description: 'One tap to cheer on friends, teammates, and rivals alike.',
      delay: 200
    },
    {
      icon: Star,
      title: 'Totally Free',
      description: 'Yep, the app (and all the new goodness) drops at a price everyone loves.',
      delay: 300
    }
  ];

  const stats = [
    { value: '2.0', label: 'Major Update', icon: TrendingUp, suffix: '' },
    { value: '100', label: 'Free Forever', icon: Star, suffix: '%' },
    { value: '∞', label: 'Highlights', icon: Camera, suffix: '' },
    { value: '🏒', label: 'Hockey Focused', icon: Trophy, suffix: '' },
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Hockey Parent',
      text: 'Finally, an app that gets it. My son\'s highlights are organized by season, and I can follow his teammates across different teams. Game changer!',
      rating: 5
    },
    {
      name: 'Mike T.',
      role: 'Youth Coach',
      text: 'Coach Biscuit is incredible. Instant drill ideas and strategy tips right when I need them. The kids love the highlight sharing too.',
      rating: 5
    },
    {
      name: 'Alex K.',
      role: 'Player',
      text: 'All my hockey stuff in one place? Yes please. The feed keeps me connected with my hockey family, even when we\'re on different teams.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 overflow-hidden">
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(20)].map((_, i) => (
          <FloatingParticle
            key={i}
            delay={i * 0.5}
            duration={15 + Math.random() * 10}
            left={Math.random() * 100}
            size={20 + Math.random() * 40}
          />
        ))}
      </div>

      {/* Header */}
      <header 
        className="bg-white/95 backdrop-blur-sm fixed top-0 left-0 right-0 z-50 shadow-lg transition-all duration-300"
        style={{
          transform: scrollY > 50 ? 'translateY(0)' : 'translateY(0)',
          boxShadow: scrollY > 50 ? '0 4px 20px rgba(0,0,0,0.1)' : '0 2px 10px rgba(0,0,0,0.05)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center animate-fade-in">
              <img src={logo} alt="Biscuit" className="w-8 h-8 animate-bounce-in" />
              <span className="ml-3 text-xl font-bold text-gray-900">Biscuit</span>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-2.5 border-2 border-blue-600 text-sm font-semibold rounded-full text-blue-600 bg-white hover:bg-blue-600 hover:text-white transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 hover:scale-105"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center"
        style={{
          transform: `translateY(${scrollY * 0.5}px)`,
          opacity: 1 - scrollY / 800
        }}
      >
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <ScrollReveal delay={0}>
            <div className="mb-8">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 mb-4 animate-pulse-slow">
                🚨 Big News for the Hockey Family 🚨
              </span>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
              Biscuit: The Hockey App
              <span className="block text-4xl md:text-6xl mt-2 bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300 bg-clip-text text-transparent animate-gradient">
                Built from the ice up.
              </span>
            </h1>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              Your all-in-one hub to follow, celebrate, and improve every hockey journey.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={300}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="https://apps.apple.com/us/app/biscuit-the-hockey-app/id6503226772"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-full text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 glow-animation"
              >
                <Download className="mr-3 group-hover:animate-bounce" size={24} />
                Download App on iOS
                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />
              </a>
              <Link
                to="/login"
                className="group inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-full text-white hover:bg-white hover:text-blue-900 transition-all duration-300 transform hover:scale-105"
              >
                Sign Up Free
                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={400}>
            <div className="flex items-center justify-center space-x-8 text-white/80">
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={20} />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={20} />
                <span>Free forever</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={20} />
                <span>Join 10,000+ players</span>
              </div>
            </div>
          </ScrollReveal>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="text-white/60" size={32} />
          </div>
        </div>
      </section>

      {/* What's New Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                🏒 What's New?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We built Biscuit from the ice up to solve the real problems hockey families face every day.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newFeatures.map((feature, index) => (
              <ScrollReveal key={index} delay={feature.delay}>
                <div
                  className="group bg-gray-50 p-8 rounded-2xl text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 hover:scale-105 border border-gray-200 relative overflow-hidden"
                >
                  {/* Hover gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                  
                  <div className={`relative w-16 h-16 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500`}>
                    <feature.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 relative z-10">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed relative z-10">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="text-center transform hover:scale-110 transition-transform duration-300">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-float">
                    <stat.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {stat.value === '∞' ? '∞' : stat.value === '2.0' ? '2.0' : stat.value === '🏒' ? '🏒' : (
                      <AnimatedCounter end={parseInt(stat.value)} suffix={stat.suffix} />
                    )}
                  </h3>
                  <p className="text-blue-100 text-lg">
                    {stat.label}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why You'll Love It Section */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-blue-50 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                🎯 Why You'll Love It
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <ScrollReveal key={index} delay={benefit.delay}>
                <div
                  className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300">
                      <benefit.icon className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                💬 Loved by the Hockey Community
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See what players, parents, and coaches are saying
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} delay={index * 150}>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Community Preview Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                🏆 See the Community in Action
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Watch highlights, celebrate wins, and connect with the hockey family
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Highlight Feed', icon: Camera, color: 'from-pink-500 to-red-500', delay: 0 },
              { title: 'Game Results', icon: Trophy, color: 'from-yellow-500 to-orange-500', delay: 100 },
              { title: 'AI Coach Chat', icon: Brain, color: 'from-blue-500 to-purple-500', delay: 200 }
            ].map((item, index) => (
              <ScrollReveal key={index} delay={item.delay}>
                <div className="group bg-white/10 backdrop-blur-sm p-8 rounded-2xl text-center hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-4 hover:scale-105 border border-white/20">
                  <div className={`w-20 h-20 bg-gradient-to-r ${item.color} rounded-full flex items-center justify-center mx-auto mb-6 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-500`}>
                    <item.icon className="text-white" size={40} />
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    {item.title}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-300">
                      <Heart className="text-red-400 animate-pulse" size={16} />
                      <span>Hype up teammates</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-300">
                      <Share2 className="text-blue-400" size={16} />
                      <span>Share epic moments</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-300">
                      <MessageCircle className="text-green-400" size={16} />
                      <span>Get instant advice</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 relative z-10 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-float" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '4s' }} />
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal>
            <div className="mb-6">
              <Sparkles className="mx-auto text-yellow-400 animate-pulse" size={48} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Join the Hockey Family?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Download Biscuit and start celebrating every hockey journey—from first goals to championship wins.
            </p>
            
            {/* Urgency indicator */}
            <div className="mb-8 inline-flex items-center px-4 py-2 rounded-full bg-yellow-400/20 border border-yellow-400/50">
              <span className="text-yellow-300 font-semibold">
                ⚡ Join 10,000+ players already on the ice
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="https://apps.apple.com/us/app/biscuit-the-hockey-app/id6503226772"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-full text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 glow-animation"
              >
                <Download className="mr-3 group-hover:animate-bounce" size={24} />
                Download Now on iOS
                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />
              </a>
              <Link
                to="/login"
                className="group inline-flex items-center justify-center px-8 py-4 border-2 border-white text-lg font-medium rounded-full text-white hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105"
              >
                Sign Up Free
                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={18} />
                <span>No credit card</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={18} />
                <span>Free forever</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={18} />
                <span>Setup in 2 minutes</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img src={logo} alt="Biscuit" className="w-8 h-8" />
              <span className="ml-3 text-lg font-semibold">Biscuit</span>
            </div>
            <div className="flex space-x-8">
              {/* Footer links can be added here */}
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>© 2024 Biscuit Hockey. Built for the hockey family, by the hockey family. 🏒</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
